import type React from 'react';
import { useEffect } from 'react';

import {
  AdornedInput,
  Box,
  FormField,
  FormHelp,
  InlineFeedback,
  Label,
  Paragraph,
  Tooltip,
} from '@zillow/constellation-10';

import type { Section } from '../../../models';
import { TENANT_DEFAULT } from '../../../constants';
import { stopPropagationIfInvalidNumber } from '../../../utils/validatorTypes/PositiveIntegerValidator';

type Props = {
  section: Section;
  setSectionValues: (keyValuePairs: object) => void;
};

export default function TenantDefault({ section, setSectionValues }: Props): JSX.Element {
  function valueIsValid(value: string): boolean {
    return Number(value) >= TENANT_DEFAULT.BOUNDS.MIN_DAYS;
  }

  useEffect(() => {
    section.setCustomFieldValidator(TENANT_DEFAULT.FIELD_NAME.UNPAID_RENT, valueIsValid);
    section.setCustomFieldValidator(TENANT_DEFAULT.FIELD_NAME.OTHER_VIOLATION, valueIsValid);
  }, []);

  function onUnpaidRentChange(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void {
    const { value } = event.currentTarget;

    setSectionValues({
      [TENANT_DEFAULT.FIELD_NAME.UNPAID_RENT]: value,
    });
  }

  function onOtherViolationChange(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void {
    const { value } = event.currentTarget;

    setSectionValues({
      [TENANT_DEFAULT.FIELD_NAME.OTHER_VIOLATION]: value,
    });
  }

  const unpaidRentHasError = section.getField(TENANT_DEFAULT.FIELD_NAME.UNPAID_RENT).hasError;
  const otherViolationHasError = section.getField(TENANT_DEFAULT.FIELD_NAME.OTHER_VIOLATION).hasError;

  return (
    <>
      <Box css={{ display: 'flex', alignItems: 'center', gap: 'tighter' }}>
        <Paragraph textStyle="body-lg-bold">
          How long is the grace period before the tenant defaults on the lease?
        </Paragraph>
        <Tooltip content="For a tenant to default on the lease, they must fail to live up to the obligations they agreed to in the lease." />
      </Box>
      <FormField
        required
        css={{ marginBottom: 'layout.tight' }}
        error={unpaidRentHasError}
        label={<Label id="unpaid-rent">For unpaid rent or utility/service fee</Label>}
        control={
          <AdornedInput.Root error={unpaidRentHasError}>
            <AdornedInput.Input
              data-test="unpaid-rent"
              aria-labelledby="unpaid-rent"
              type="number"
              onChange={onUnpaidRentChange}
              onKeyDown={stopPropagationIfInvalidNumber}
              value={section.getFieldValue(TENANT_DEFAULT.FIELD_NAME.UNPAID_RENT)}
            />
            <AdornedInput.Adornment>days after receiving notice</AdornedInput.Adornment>
          </AdornedInput.Root>
        }
        description={
          <>
            {unpaidRentHasError ? (
              <FormHelp>Enter a number higher than {TENANT_DEFAULT.BOUNDS.MIN_DAYS}</FormHelp>
            ) : (
              <FormHelp>{TENANT_DEFAULT.BOUNDS.MIN_DAYS} days is the minimum allowed and most common</FormHelp>
            )}
          </>
        }
      />
      <FormField
        required
        css={{ marginBottom: 'layout.tight' }}
        error={otherViolationHasError}
        label={<Label id="other-violation">For any other violation that isn’t fixed</Label>}
        control={
          <AdornedInput.Root error={otherViolationHasError}>
            <AdornedInput.Input
              data-test="other-violation"
              aria-labelledby="other-violation"
              type="number"
              onChange={onOtherViolationChange}
              onKeyDown={stopPropagationIfInvalidNumber}
              value={section.getFieldValue(TENANT_DEFAULT.FIELD_NAME.OTHER_VIOLATION)}
            />
            <AdornedInput.Adornment>days after receiving notice</AdornedInput.Adornment>
          </AdornedInput.Root>
        }
        description={
          <>
            {otherViolationHasError ? (
              <FormHelp>Enter a number higher than {TENANT_DEFAULT.BOUNDS.MIN_DAYS}</FormHelp>
            ) : (
              <FormHelp>{TENANT_DEFAULT.BOUNDS.MIN_DAYS} days is the minimum allowed. 30 days is most common.</FormHelp>
            )}
          </>
        }
      />
      <InlineFeedback>
        Under the lease terms, if a tenant is in default, you must notify them before exercising any other rights under
        the lease
      </InlineFeedback>
    </>
  );
}
